import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Sparkles, Users, MessageSquare, Layers } from 'lucide-react';
import { useObsGeminiActive, useObsAdoptionByArea } from '@/hooks/useObservability';
import { StatCard, LoaderBlock, fmtNum } from './shared';

export function ObsGeminiTab() {
  const { t } = useTranslation();
  const { data: events = [], isLoading } = useObsGeminiActive();
  const { data: adoption = [] } = useObsAdoptionByArea();

  const byBucket = useMemo(() => {
    const map = new Map<string, { bucket: string; events: number; users: Set<string> }>();
    for (const r of events) {
      const cur = map.get(r.usage_bucket) || { bucket: r.usage_bucket, events: 0, users: new Set<string>() };
      cur.events += 1;
      cur.users.add(r.user_email);
      map.set(r.usage_bucket, cur);
    }
    return Array.from(map.values()).map((r) => ({ bucket: r.bucket, events: r.events, users: r.users.size }));
  }, [events]);

  const byApp = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of events) {
      if (!r.app_name) continue;
      map.set(r.app_name, (map.get(r.app_name) || 0) + 1);
    }
    return Array.from(map.entries())
      .map(([app, n]) => ({ app, n }))
      .sort((a, b) => b.n - a.n).slice(0, 10);
  }, [events]);

  const adoptionByArea = useMemo(() => {
    const map = new Map<string, { area: string; chat: number; ws: number; users: number }>();
    for (const r of adoption.filter((a) => a.source === 'gemini_chat' || a.source === 'gemini_workspace')) {
      const cur = map.get(r.area) || { area: r.area, chat: 0, ws: 0, users: 0 };
      if (r.source === 'gemini_chat') cur.chat += r.events;
      if (r.source === 'gemini_workspace') cur.ws += r.events;
      cur.users = Math.max(cur.users, r.users);
      map.set(r.area, cur);
    }
    return Array.from(map.values()).sort((a, b) => (b.chat + b.ws) - (a.chat + a.ws)).slice(0, 12);
  }, [adoption]);

  if (isLoading) return <LoaderBlock />;

  const totalEvents = byBucket.reduce((s, r) => s + r.events, 0);
  const totalUsers = new Set(events.map((e) => e.user_email)).size;
  const chatUsers = byBucket.find((b) => b.bucket === 'gemini_chat')?.users || 0;
  const wsUsers = byBucket.find((b) => b.bucket === 'workspace_active_usage')?.users || 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Sparkles} tone="green" label={t('observability.gemini.stat_events')} value={fmtNum(totalEvents)}
                  sub={t('observability.gemini.stat_events_sub')} />
        <StatCard icon={Users} tone="blue" label={t('observability.gemini.stat_users')} value={fmtNum(totalUsers)} />
        <StatCard icon={MessageSquare} tone="purple" label={t('observability.gemini.stat_chat_users')} value={fmtNum(chatUsers)} />
        <StatCard icon={Layers} tone="amber" label={t('observability.gemini.stat_workspace_users')} value={fmtNum(wsUsers)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">{t('observability.gemini.events_by_bucket')}</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={byBucket}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="events" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">{t('observability.gemini.top_apps')}</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={byApp} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="app" width={80} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="n" fill="#3b82f6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">{t('observability.gemini.adoption_by_area')}</h3>
        <ResponsiveContainer width="100%" height={Math.max(280, adoptionByArea.length * 26)}>
          <BarChart data={adoptionByArea} layout="vertical" margin={{ left: 130 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis type="category" dataKey="area" width={130} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="chat" stackId="g" fill="#10b981" name="Gemini chat" />
            <Bar dataKey="ws" stackId="g" fill="#3b82f6" name="Workspace AI" />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
